import { Box, Drawer } from "@mui/material";
import "./ConfigurationDrawer.scss";
import { ChoiceChipGroup, Typography } from "@chr/eds-react";
import { UserWidget } from "@/common/constants/online.homepagegateway.api";
import { useEffect, useState } from "react";
import { modeFilterSet, riskLevelFilterSet, statusFilterSet, dateRangesFilterSet } from "@/common/constants/filterItem";
import { ChoiceChipInGroupProps } from "@chr/eds-react/ChoiceChipGroup/ChoiceChipGroupProps";
import { useDispatch } from 'react-redux';
import { updateWidgetInit } from "@/common/actions/widgetActions";
import { ConfigurationDrawerProps } from "./ConfigurationDrawerProps";
import ConfigurationDrawerHeader from "./ConfigurationDrawerHeader";
import ConfigurationDrawerTitle from "./ConfigurationDrawerTitle";
import ConfigurationDrawerButtons from "./ConfigurationDrawerButtons";
import { useTranslation } from "@chr/online-shared-ui-localizations";

const LEGACY_CARRIER_BOOKED_KEY = 'Carrier Booked';
const BOOKED_KEY = 'Booked';

const dedupeByValue = (values: string[]) => Array.from(new Set(values));

const canonicalizeStatuses = (statuses: { key: string; active: boolean }[]) => {
  const mergedByKey = new Map<string, { key: string; active: boolean }>();

  statuses.forEach((status) => {
    const canonicalKey = status.key === LEGACY_CARRIER_BOOKED_KEY ? BOOKED_KEY : status.key;
    const existing = mergedByKey.get(canonicalKey);

    if (existing) {
      existing.active = existing.active || status.active;
      return;
    }

    mergedByKey.set(canonicalKey, { ...status, key: canonicalKey });
  });

  return Array.from(mergedByKey.values());
};

export const OrdersShipmentConfigurationDrawer = ({
  handleToggle,
  open,
  widget,
}: ConfigurationDrawerProps) => {
  const translate = useTranslation();
  const [widgetTitle, setWidgetTitle] = useState<string>('');
  const [dateRangeChips, setDateRangeChips] = useState<ChoiceChipInGroupProps[]>([]);
  const [statusChips, setStatusChips] = useState<ChoiceChipInGroupProps[]>([]);
  const [modeChips, setModeChips] = useState<ChoiceChipInGroupProps[]>([]);
  const [riskLevelChips, setRiskLevelChips] = useState<ChoiceChipInGroupProps[]>([]);
  const [selectedDateRangeChips, setSelectedDateRangeChips] = useState<string[]>([]);
  const [selectedStatusChips, setSelectedStatusChips] = useState<string[]>([]);
  const [selectedModeChips, setSelectedModeChips] = useState<string[]>([]);
  const [selectedRiskLevelChips, setSelectedRiskLevelChips] = useState<string[]>([]);
  const dispatch = useDispatch();

  useEffect(() => {
    getWidgetTitle();
    getDateRangeChips();
    getStatusChips();
    getModeChips();
    getRiskLevelChips();
  }, [widget]);

  const getDateRangeChips = () => {
    const chips: ChoiceChipInGroupProps[] = [];
    const selectedDateRangeChips: string[] = [];
    dateRangesFilterSet.forEach((option) => {
      const dateRange = widget.dataModelOptions?.timeFrames?.find(x => x.key === option.key);

      chips.push({ label: translate(option.label)});

      if (dateRange?.active) {
        selectedDateRangeChips.push(option.label);
      }
    });
    setDateRangeChips(chips);
    setSelectedDateRangeChips(selectedDateRangeChips);
  }

  const getStatusChips = () => {
    const chips: ChoiceChipInGroupProps[] = [];
    const selectedStatusChips: string[] = [];
    statusFilterSet?.forEach((filter) => {
      const isActive = filter.key === BOOKED_KEY
        ? !!widget.dataModelOptions?.statuses?.some((item) =>
            (item.key === BOOKED_KEY || item.key === LEGACY_CARRIER_BOOKED_KEY) && item.active
          )
        : !!widget.dataModelOptions?.statuses?.find((item) => item.key === filter.key)?.active;

      chips.push({ label: translate(filter.label)});

      if (isActive) {
        selectedStatusChips.push(filter.label);
      }
    });

    setStatusChips(chips);
    setSelectedStatusChips(dedupeByValue(selectedStatusChips));
  }

  const getModeChips = () => {
    const chips: ChoiceChipInGroupProps[] = [];
    const selectedModeChips: string[] = [];
    modeFilterSet?.forEach((filter) => {
      const shippingMethod = widget.dataModelOptions?.shippingMethods?.find(x => x.key === filter.key);

      chips.push({ label: translate(filter.label)});

      if (shippingMethod?.active) {
        selectedModeChips.push(filter.label);
      }
    });

    setModeChips(chips);
    setSelectedModeChips(selectedModeChips);
  }

  const getRiskLevelChips = () => {
    const chips: ChoiceChipInGroupProps[] = [];
    const selectedRiskLevelChips: string[] = [];
    riskLevelFilterSet?.forEach((filter) => {
      const riskLevel = widget.dataModelOptions?.riskLevels?.find((riskLevel) => riskLevel.key === filter.key);

      chips.push({ label: translate(filter.label)});

      if (riskLevel?.active) {
        selectedRiskLevelChips.push(filter.label);
      }
    });

    setRiskLevelChips(chips);
    setSelectedRiskLevelChips(selectedRiskLevelChips);
  }

  const updateWidget = () => {
    const riskLevels = widget.dataModelOptions?.riskLevels?.map(riskLevel => {
      const item = riskLevelFilterSet.find((item) => item.key === riskLevel.key);
      if (item && selectedRiskLevelChips.includes(item.label)) {
        return {...riskLevel, active: true };
      }
      return { ...riskLevel, active: false };
    });

    const statuses = widget.dataModelOptions?.statuses?.map(status => {
      const item = statusFilterSet.find((item) => item.key === status.key);
      if (status.key === LEGACY_CARRIER_BOOKED_KEY) {
        return { ...status, active: selectedStatusChips.includes(BOOKED_KEY) };
      }

      if (item && selectedStatusChips.includes(item.label)) {
        return {...status, active: true };
      }
      return { ...status, active: false };
    });
    const dedupedStatuses = statuses ? canonicalizeStatuses(statuses) : statuses;

    const shippingMethods = widget.dataModelOptions?.shippingMethods?.map(mode => {
      const item = modeFilterSet.find((item) => item.key === mode.key);
      if (item && selectedModeChips.includes(item.label)) {
        return {...mode, active: true };
      }
      return { ...mode, active: false };
    });

    const dateRanges = widget.dataModelOptions?.timeFrames?.map(timeFrame => {
      const item = dateRangesFilterSet.find((item) => item.key === timeFrame.key);
      if (item && selectedDateRangeChips.includes(item.label)) {
        return {...timeFrame, active: true };
      }
      return { ...timeFrame, active: false };
    });

    const updatedWidget = {
      id: widget.id,
      name: widget.name,
      widgetId: widget.widgetId,
      title: widgetTitle,
      layouts: widget.layouts,
      currentLayout: widget.currentLayout,
      dataModelOptions: {
        statuses: dedupedStatuses,
        shippingMethods: shippingMethods,
        riskLevels: riskLevels,
        timeFrames: dateRanges,
      }
    } as UserWidget;

    dispatch(updateWidgetInit(updatedWidget));
    handleToggle();
  }

  const getWidgetTitle = () => {
    setWidgetTitle(widget.title);
  }

  const cancelChanges = () => {
    getWidgetTitle();
    getDateRangeChips();
    getStatusChips();
    getModeChips();
    getRiskLevelChips();
    handleToggle();
  };

  const responsiveDrawer = (
    <div className="configuration-wrapper">
      <ConfigurationDrawerHeader handleToggle={handleToggle} />
      <ConfigurationDrawerTitle widgetTitle={widgetTitle} setWidgetTitle={setWidgetTitle} />
      <Box className="configuration-body">
        <Typography variant="subtitle100" fontWeight="semiBold">
          {translate('Select summary cards to display')}
        </Typography>
        <Box className="configuration-chips-header">
          <Typography variant="subtitle200">
            {translate('Date range')}
          </Typography>
        </Box>
        <ChoiceChipGroup 
          chips={dateRangeChips}
          initialSelection={selectedDateRangeChips.map(key => translate(key))}
          multiSelect
          onChange={
            (selected) => {
              // Convert translated labels back to untranslated keys for storage
              const untranslatedKeys = selected.map(translatedLabel => {
                const item = dateRangesFilterSet.find(f => translate(f.label) === translatedLabel);
                return item?.label ?? translatedLabel;
              });
              setSelectedDateRangeChips(dedupeByValue(untranslatedKeys));
            }
          }
        />
        <Box className="configuration-chips-header">
          <Typography variant="subtitle200">
            {translate('Status')}
          </Typography>
        </Box>
        <ChoiceChipGroup 
          chips={statusChips}
          initialSelection={selectedStatusChips.map(key => translate(key))}
          multiSelect
          onChange={
            (selected) => {
              // Convert translated labels back to untranslated keys for storage
              const untranslatedKeys = selected.map(translatedLabel => {
                const item = statusFilterSet.find(f => translate(f.label) === translatedLabel);
                return item?.label ?? translatedLabel;
              });
              setSelectedStatusChips(dedupeByValue(untranslatedKeys));
            }
          }
        />
        <Box className="configuration-chips-header">
          <Typography variant="subtitle200">
            {translate('Mode')}
          </Typography>
        </Box>
        <ChoiceChipGroup 
          chips={modeChips}
          initialSelection={selectedModeChips.map(key => translate(key))}
          multiSelect
          onChange={
            (selected) => {
              // Convert translated labels back to untranslated keys for storage
              const untranslatedKeys = selected.map(translatedLabel => {
                const item = modeFilterSet.find(f => translate(f.label) === translatedLabel);
                return item?.label ?? translatedLabel;
              });
              setSelectedModeChips(dedupeByValue(untranslatedKeys));
            }
          }
        />
        <Box className="configuration-chips-header">
          <Typography variant="subtitle200" fontWeight="semiBold">
            {translate('Risk level')}
          </Typography>
        </Box>
        <ChoiceChipGroup 
          chips={riskLevelChips}
          initialSelection={selectedRiskLevelChips.map(key => translate(key))}
          multiSelect
          onChange={
            (selected) => {
              // Convert translated labels back to untranslated keys for storage
              const untranslatedKeys = selected.map(translatedLabel => {
                const item = riskLevelFilterSet.find(f => translate(f.label) === translatedLabel);
                return item?.label ?? translatedLabel;
              });
              setSelectedRiskLevelChips(dedupeByValue(untranslatedKeys));
            }
          }
        />
      </Box>
      <ConfigurationDrawerButtons updateWidget={updateWidget} cancelChanges={cancelChanges} />
    </div>
  );

  return (
    <Drawer 
      open={open}
      onClose={cancelChanges}
      anchor="right"
      PaperProps={{
        square: false
      }}
    >
      {responsiveDrawer}
    </Drawer>
  );
}

export default OrdersShipmentConfigurationDrawer;